import { CallScreenMarker, callScreenDidLayout } from 'APP/Lib/CallScreenLayout'
import React, { useCallback, useEffect } from 'react'

import I18n, { isTranslationKey } from 'APP/I18n'
import { isAndroid } from 'APP/Helpers/checkPlatform'
import { View, Image, Keyboard, type ImageStyle } from 'react-native'
import { useDispatch } from 'APP/Hooks/redux'
import { Images } from 'APP/Themes'
import Modal from 'APP/Converse/Modal'
import DialogueSound from 'APP/Components/DialogueSound'

// Styles
import Styles, { StyledLinearGradient } from './style'
import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import GlowingHalo from 'APP/Components/GlowingHalo'
import { videoSessionActions } from 'APP/Store/VideoSession'
import { getBrandConfig } from 'APP/Lib/ConfigHelpers'
import Config from 'APP/Config'
import type { SupportedLocales } from 'APP/Models/Locale'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import Analytics from 'APP/Services/Analytics'

export type IncomingCallScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'incomingCall'
>

const IncomingCallScreen = (props: IncomingCallScreenProps) => {
  const { caller, isFullScreen, videoSessionId } = props.route.params
  const { avatar_url, mainSpecialization } = caller || {}
  const specializationKey = `Specialties.${mainSpecialization}`
  const dispatch = useDispatch()

  useEffect(() => {
    Keyboard.dismiss()
  }, [])

  const acceptCall = useCallback(() => {
    const lastCallAcceptedAt = Date.now()
    Analytics.trackEvent('video_call_accepted', {
      session_id: videoSessionId,
      source: 'incoming_call_screen',
    })
    dispatch(
      videoSessionActions.patientAcceptedCall({
        lastCallAcceptedAt,
        onDemandSessionId: videoSessionId,
      })
    )
  }, [dispatch, videoSessionId])

  const endCall = useCallback(() => {
    Analytics.trackEvent('video_call_declined', {
      session_id: videoSessionId,
      source: 'incoming_call_screen',
    })
    dispatch(
      videoSessionActions.patientDeclinedCall({ onDemandSessionId: videoSessionId ?? undefined })
    )
  }, [dispatch, videoSessionId])

  const ScreenWrapper = isFullScreen ? StyledLinearGradient : Modal

  return (
    <ScreenWrapper variant="oomph">
      <DialogueSound name="ringing" />

      <View
        nativeID={isAndroid() ? CallScreenMarker.Incoming : undefined}
        onLayout={() => callScreenDidLayout(CallScreenMarker.Incoming)}
        style={Styles.centeredContainer}
      >
        <Typography variant="h3" color="darkText">
          {I18n.t('IncomingCallScreen.title')}
        </Typography>
        <View style={Styles.avatarContainer}>
          <GlowingHalo />
          <Image source={{ uri: avatar_url }} style={Styles.roundImage as ImageStyle} />
        </View>
        <Typography variant="h2" color="darkText">
          {/** TODO: add handling logic to getBrandConfig for unsupported locales, instead of casting here */}
          {getBrandConfig(Config.BRAND_ID, I18n.locale as SupportedLocales).productName}
        </Typography>
        {mainSpecialization ? (
          <Typography color="dim" variant="subheaderBold">
            {isTranslationKey(specializationKey) ? I18n.t(specializationKey) : undefined}
          </Typography>
        ) : (
          <Typography variant="subheaderBold" color="darkText">
            &#160;
          </Typography>
        )}

        <View style={Styles.spacedColumnContainer}>
          <Typography variant="bodyNormal" color="darkText">
            {I18n.t('IncomingCallScreen.body')}
          </Typography>
        </View>

        <View style={Styles.spacedRowContainer}>
          <View style={Styles.buttonContainer}>
            <Button
              variant="error"
              widthVariant="circle"
              icon={Images.hangUp}
              iconVariant="image"
              onPress={endCall}
              analyticsName="Decline call"
              style={Styles.button}
              testID="decline-call"
              hasShadow
            />
            <Typography variant="subheader" color="darkText">
              {I18n.t('IncomingCallScreen.cancel')}
            </Typography>
          </View>

          <View style={Styles.buttonContainer}>
            <Button
              variant="success"
              widthVariant="circle"
              icon={Images.video}
              iconVariant="image"
              onPress={acceptCall}
              analyticsName="Accept call"
              style={Styles.button}
              testID="accept-call"
              hasShadow
            />
            <Typography variant="subheader" color="darkText">
              {I18n.t('IncomingCallScreen.answer')}
            </Typography>
          </View>
        </View>
      </View>
    </ScreenWrapper>
  )
}

export default IncomingCallScreen
